import { useNavigate, useParams } from 'react-router-dom';
import { OptionSelector } from '../../../components/option-selector/OptionSelector';
import { useOperatorsState } from '../../../services/OperatorsList';
import { ManageDevicesAction } from './action/ManageDevicesAction';
import { useToast } from '../../../services/ToastContext';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, BluetoothOff, Eraser, Lock, ShieldCheck, Smartphone } from 'lucide-react';
import './ManageDevices.scss';

type Props = {
  deviceData: any;
};

export const ManageDevices = (props: Props) => {
  const navigate = useNavigate();
  const { operatorList, loggedUserData } = useOperatorsState();
  const { operatorId, cpeId, deviceId } = useParams();
  const { showToastMessage } = useToast();
  const { t } = useTranslation();
  const enrolledMdmRef: any = useRef(null);
  const bluetoothDisabledRef: any = useRef(null);
  const factoryResetDisabledRef: any = useRef(null);
  const uninstallAppsDisabledRef: any = useRef(null);
  const lockDeviceRef: any = useRef(null);
  const remoteWipeRef: any = useRef(null);

  useEffect(() => {
    console.log('deviceData', props.deviceData);
  }, []);

  function disableAllActions() {
    const changes =
      enrolledMdmRef.current.action ||
      bluetoothDisabledRef.current.action ||
      factoryResetDisabledRef.current.action ||
      uninstallAppsDisabledRef.current.action;

    if (!changes) {
      enrolledMdmRef?.current?.setAction(false);
      bluetoothDisabledRef?.current?.setAction(false);
      factoryResetDisabledRef?.current?.setAction(false);
      uninstallAppsDisabledRef?.current?.setAction(false);
    } else {
      enrolledMdmRef?.current?.setAction(false);
      bluetoothDisabledRef?.current?.setAction(false);
      factoryResetDisabledRef?.current?.setAction(false);
      uninstallAppsDisabledRef?.current?.setAction(false);
      showToastMessage(t('deviceDetails.manage.toasts.changesCancelled'));
    }
  }

  function disableAllMdm() {
    const changes = lockDeviceRef.current.action || remoteWipeRef.current.action;

    if (!changes) {
      lockDeviceRef?.current?.setAction(false);
      remoteWipeRef?.current?.setAction(false);
    } else {
      lockDeviceRef.current.setAction(false);
      remoteWipeRef.current.setAction(false);
      showToastMessage(t('deviceDetails.manage.toasts.changesCancelled'));
    }
  }

  async function submit() {
    if (lockDeviceRef?.current?.action) {
      try {
        await axios.post(`/op/devices/${props.deviceData.espID}/lock`);
      } catch (err) {
        console.log(err);
        showToastMessage(t('deviceDetails.manage.toasts.lockError'));
      }

      lockDeviceRef?.current?.setAction(false);
      remoteWipeRef?.current?.setAction(false);
      showToastMessage(t('deviceDetails.manage.toasts.commandsSent'));
    }
  }

  async function applyChanges() {
    if (enrolledMdmRef?.current?.action) {
      console.log('do something');
    } else {
      showToastMessage(t('deviceDetails.manage.toasts.lockError'));
    }
    if (bluetoothDisabledRef?.current?.action) {
      console.log('do something to bluetooth');
    } else {
      showToastMessage(t('deviceDetails.manage.toasts.lockError'));
    }
    if (factoryResetDisabledRef?.current?.action) {
      console.log('do something to factory reset');
    } else {
      showToastMessage(t('deviceDetails.manage.toasts.lockError'));
    }
    if (uninstallAppsDisabledRef?.current?.action) {
      console.log('do something to uninstall apps');
      showToastMessage(t('deviceDetails.manage.toasts.lockError'));
    } else {
    }

    enrolledMdmRef?.current?.setAction(false);
    bluetoothDisabledRef?.current?.setAction(false);
    factoryResetDisabledRef?.current?.setAction(false);
    uninstallAppsDisabledRef?.current?.setAction(false);
    showToastMessage(t('deviceDetails.manage.toasts.commandsSent'));
  }

  const handleEnableBluetooth = async () => {
    try {
      const espID = props.deviceData?.espID;
      await axios.post(`devices/${espID}/enable-bluetooth`);
    } catch (error) {
      console.error('Error enabling bluetooth: ', error);
    }
  };

  const handleDisableBluetooth = async () => {
    try {
      const espID = props.deviceData?.espID;
      await axios.post(`devices/${espID}/disable-bluetooth`);
    } catch (error) {
      console.error('Error disabling bluetooth: ', error);
    }
  };

  // Chip tone for the MDM enrolment state (mint = attached, danger = detached, warn = registered).
  let mdmStateColor;
  let mdmState = props?.deviceData?.mdmState ? props?.deviceData?.mdmState : 'N/A';
  if (mdmState === 'N/A') {
    mdmStateColor = '';
  } else if (mdmState === 'ATTACHED') {
    mdmStateColor = 'mint';
  } else if (mdmState === 'DETACHED') {
    mdmStateColor = 'danger';
  } else if (mdmState === 'REGISTERED') {
    mdmStateColor = 'warn';
  }
  // Display label only; mdmState itself stays the raw API value
  const mdmStateLabel =
    mdmState === 'N/A'
      ? t('deviceDetails.common.notAvailable')
      : t(`deviceDetails.mdmStates.${mdmState}`, { defaultValue: mdmState });

  return (
    <section className="dd-manage">
      <div className="dd-manage-section">
        <header className="dd-manage-hd">
          <span className="dd-manage-hd-icon" aria-hidden="true">
            <ShieldCheck size={18} strokeWidth={1.75} />
          </span>
          <div className="dd-manage-hd-text">
            <h2 className="bd-h2">{t('deviceDetails.manage.policyTitle')}</h2>
            <p className="bd-meta">{t('deviceDetails.manage.policySubtitle')}</p>
          </div>
        </header>

        <div className="dd-manage-grid">
          <ManageDevicesAction
            title={t('deviceDetails.manage.enrolledTitle')}
            ref={enrolledMdmRef}
            icon={<Smartphone size={16} strokeWidth={1.75} />}
            subtitle={t('deviceDetails.manage.enrolledSubtitle')}
            hideSwitch={true}
            switchReplacementText={mdmStateLabel}
            switchReplacementTextColor={mdmStateColor}
          ></ManageDevicesAction>
          <ManageDevicesAction
            title={t('deviceDetails.manage.bluetoothTitle')}
            ref={bluetoothDisabledRef}
            icon={<BluetoothOff size={16} strokeWidth={1.75} />}
            subtitle={t('deviceDetails.manage.bluetoothSubtitle')}
            actionDisable={handleEnableBluetooth}
            actionEnable={handleDisableBluetooth}
          ></ManageDevicesAction>
        </div>
        {/* <ManageDevicesAction
          title="Factory Reset Disabled?"
          classes="mt-5"
          ref={factoryResetDisabledRef}
          subtitle="Prevents user from performing a factory reset on the device."
        ></ManageDevicesAction>
        <ManageDevicesAction
          title="Uninstall Applications Disabled?"
          classes="mt-5"
          ref={uninstallAppsDisabledRef}
          subtitle="Prevents user from removing applications from the device."
        ></ManageDevicesAction>

        <div className="mt-6">
          <div className="flex gap-2">
            <span className="font-medium ">Camera Access</span>
          </div>

          <span className="text-sm w-64">Controls access to the device camera application.</span>

          <OptionSelector
            options={[
              { label: 'Unspecified', value: 0 },
              { label: 'User Choice', value: 1 },
              { label: 'Disabled', value: 2 },
              { label: 'Enforced', value: 3 },
            ]}
          ></OptionSelector>
        </div> */}

        {/* <footer className="flex justify-around mt-12">
          <button onClick={applyChanges} style={{ background: '#81DAB9' }} className="rounded-xl px-5 py-2 font-medium w-44">
            Apply Changes
          </button>
          <button onClick={disableAllActions} style={{ background: '#BBCEDB' }} className="rounded-xl px-5 py-2 font-medium w-44">
            Cancel Changes
          </button>
        </footer> */}
      </div>

      <div className="dd-manage-section">
        <header className="dd-manage-hd">
          <span className="dd-manage-hd-icon danger" aria-hidden="true">
            <AlertTriangle size={18} strokeWidth={1.75} />
          </span>
          <div className="dd-manage-hd-text">
            <h2 className="bd-h2">{t('deviceDetails.manage.actionsTitle')}</h2>
            <p className="bd-meta">{t('deviceDetails.manage.actionsWarning')}</p>
          </div>
        </header>

        <div className="dd-manage-grid">
          <ManageDevicesAction
            ref={lockDeviceRef}
            //actionEnable={lockDevice}
            title={t('deviceDetails.manage.lockTitle')}
            icon={<Lock size={16} strokeWidth={1.75} />}
            subtitle={t('deviceDetails.manage.lockSubtitle')}
          ></ManageDevicesAction>
          <ManageDevicesAction
            ref={remoteWipeRef}
            title={t('deviceDetails.manage.wipeTitle')}
            tone="danger"
            icon={<Eraser size={16} strokeWidth={1.75} />}
            subtitle={t('deviceDetails.manage.wipeSubtitle')}
          ></ManageDevicesAction>
        </div>
        {/* <footer className="flex justify-around mt-12 ">
          <button onClick={submit} style={{ background: '#81DAB9' }} className="rounded-xl px-5 py-2 font-medium w-44">
            Submit
          </button>
          <button onClick={disableAllMdm} style={{ background: '#BBCEDB' }} className="rounded-xl px-5 py-2 font-medium w-44">
            Cancel Changes
          </button>
        </footer> */}
      </div>
    </section>
  );
};
